import type { AjoutControle, ChangementTraduction, Invariant } from '@rosetta/shared';
import { useId } from 'react';
import { TexteInvariants } from './texte-invariants';
import { TexteTraduitAjouts } from './texte-traduit-ajouts';

interface ColonneTexteProps {
  /** "Texte d'origine" or "Texte traduit". */
  titre: string;
  texte: string;
  /** The invariants to mark in the text, when the analysis gave some. */
  invariants?: readonly Invariant[] | null | undefined;
  /** The additions of the control to mark in the text (#23): only the translated text has some. */
  ajouts?: readonly AjoutControle[] | undefined;
  /** The changes the translation declares, to mark in the translated text (#28). */
  changements?: readonly ChangementTraduction[] | undefined;
  /** The passages of the "Bloquant" points of other indicators, marked in "Texte traduit". */
  passages?: readonly string[] | undefined;
}

/**
 * One column of "Avant / après": the whole text, as it is, in a frame. Line breaks are kept
 * (`whitespace-pre-wrap`), nothing is cut, and the page scrolls. The invariants of the
 * analysis are marked in it (#9); the characters themselves are never changed. The translated
 * text is the same component in "Avant / après" and in "Texte traduit" (#35): the same marks, the
 * same bubbles.
 */
export function ColonneTexte({
  titre,
  texte,
  invariants,
  ajouts,
  changements,
  passages,
}: ColonneTexteProps) {
  const idTitre = useId();
  return (
    <section aria-labelledby={idTitre} className="min-w-0">
      <h2 id={idTitre} className="font-semibold text-body-lg leading-[1.3]">
        {titre}
      </h2>
      <p className="mt-3 whitespace-pre-wrap break-words rounded-md border border-gray-200 px-6 py-6 font-light text-body leading-[1.7]">
        {(ajouts === undefined || ajouts.length === 0) &&
        (changements === undefined || changements.length === 0) &&
        (passages === undefined || passages.length === 0) ? (
          <TexteInvariants texte={texte} invariants={invariants} />
        ) : (
          <TexteTraduitAjouts
            texte={texte}
            invariants={invariants}
            ajouts={ajouts ?? []}
            changements={changements}
            passages={passages}
          />
        )}
      </p>
    </section>
  );
}
